import { useEffect, useState } from "react";
import { KeyRound, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

// تبويب «بوابة الموظفين»: رمز المدرسة وتفعيل دخول الموظفين وحالة آخر دخول
// (باقة البريميوم فأعلى — يُغلَّف بـ PlanGate من لوحة التحكم)
export default function StaffPortalGateTab() {
  const [overview, setOverview] = useState(null);
  const [portal, setPortal] = useState({});
  const [rows, setRows] = useState(null);

  const load = () => {
    api.get("/portal/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/portal/employees")
      .then((r) => {
        setPortal(Object.fromEntries(r.data.map((p) => [p.id, p])));
        setRows(r.data);
      })
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); }, []);

  const togglePortal = async (emp) => {
    const cur = !!portal[emp.id]?.portal_enabled;
    setPortal((p) => ({ ...p, [emp.id]: { ...p[emp.id], portal_enabled: !cur } }));
    try {
      const r = await api.post(`/portal/employees/${emp.id}/toggle`);
      setPortal((p) => ({ ...p, [emp.id]: { ...p[emp.id], portal_enabled: r.data.portal_enabled } }));
      toast.success(r.data.portal_enabled
        ? `فُعّل حساب ${emp.name} في بوابة الموظفين`
        : `عُطّل حساب ${emp.name} في بوابة الموظفين`);
    } catch {
      setPortal((p) => ({ ...p, [emp.id]: { ...p[emp.id], portal_enabled: cur } }));
      toast.error("فشل تغيير الحالة");
    }
  };

  const portalUrl = `${window.location.origin}/staff`;

  const whatsappShare = () => {
    const msg = [
      "السلام عليكم ورحمة الله وبركاته،",
      "بوابة الموظفين — منصة راصد حضوري",
      "",
      "للاطلاع على المساءلات والتعاميم والتنبيهات والرد عليها إلكترونياً:",
      `1) افتح الرابط المباشر: ${portalUrl}`,
      "2) أدخل رقم هويتك الوطنية",
      `3) أدخل رمز المدرسة: ${overview?.school_code || "......"}`,
      "",
      "ثم فعّل الإشعارات من داخل البوابة لتصلك المستجدات فوراً.",
    ].join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="bg-white rounded-2xl border shadow-sm" data-testid="staff-portal-gate-tab">
      <div className="p-4 border-b flex flex-wrap items-center gap-4 bg-emerald-50/60" data-testid="portal-code-section">
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-emerald-800" />
          <div>
            <p className="text-[11px] text-slate-400">رمز المدرسة — شاركه مع منسوبيك للدخول عبر /staff</p>
            <p className="font-mono text-2xl font-extrabold text-emerald-950 tracking-[0.3em]" dir="ltr"
              data-testid="portal-school-code">{overview?.school_code || "......"}</p>
            <p className="font-mono text-xs font-bold text-emerald-800 select-all mt-1" dir="ltr"
              data-testid="portal-direct-link">{portalUrl.replace(/^https?:\/\//, "")}</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1"
          data-testid="portal-copy-code"
          onClick={() => { navigator.clipboard.writeText(overview?.school_code || ""); toast.success("تم نسخ الرمز"); }}>
          <Copy className="w-3.5 h-3.5" /> نسخ الرمز
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1"
          data-testid="portal-copy-link"
          onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("تم نسخ الرابط"); }}>
          <Copy className="w-3.5 h-3.5" /> نسخ الرابط
        </Button>
        <Button size="sm" variant="outline" onClick={whatsappShare}
          data-testid="portal-share-whatsapp"
          className="h-8 text-xs gap-1 border-green-300 text-green-700 hover:bg-green-50">
          <Share2 className="w-3.5 h-3.5" /> مشاركة واتساب
        </Button>
      </div>

      <div className="p-4 border-b">
        <p className="text-sm font-bold text-emerald-950">تفعيل دخول الموظفين للبوابة</p>
        <p className="text-[11px] text-slate-400 mt-0.5">
          فعّل دخول كل موظف بمفتاحه ليتمكن من الاطلاع على مساءلاته والرد عليها وتوقيع التعاميم إلكترونياً
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="table-portal-gate">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs">
              {["م", "اسم الموظف", "رقم الهوية", "بوابة الموظف", "آخر دخول"].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold text-center whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={5} className="text-center py-10 text-slate-400">جارٍ التحميل...</td></tr>}
            {rows !== null && rows.length === 0 &&
              <tr><td colSpan={5} className="text-center py-10 text-slate-400">لا يوجد موظفون — أضفهم أولاً من تبويب «الموظفون»</td></tr>}
            {rows && rows.map((e, i) => (
              <tr key={e.id} className="border-t hover:bg-slate-50/60" data-testid={`portal-gate-row-${e.id}`}>
                <td className="px-3 py-2.5 text-xs text-slate-400 font-mono text-center w-10">{i + 1}</td>
                <td className="px-4 py-2.5 font-semibold whitespace-nowrap text-center">{e.name}</td>
                <td className="px-4 py-2.5 text-slate-500 font-mono text-xs text-center" dir="ltr">{e.employee_id || "—"}</td>
                <td className="px-4 py-2.5 text-center">
                  <Switch checked={!!portal[e.id]?.portal_enabled} onCheckedChange={() => togglePortal(e)}
                    data-testid={`switch-portal-${e.id}`} aria-label={`تفعيل بوابة الموظف لـ ${e.name}`} />
                </td>
                <td className="px-4 py-2.5 text-center">
                  {portal[e.id]?.last_portal_login
                    ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                        data-testid={`portal-last-login-${e.id}`}>دخل {(portal[e.id].last_portal_login || "").slice(0, 10)}</span>
                    : <span className="text-[10px] font-bold text-slate-400 bg-slate-50 border rounded-full px-2 py-0.5"
                        data-testid={`portal-last-login-${e.id}`}>لم يدخل بعد</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
